<!--
  OrderTypeTabs: On-Demand / Dedicated, at the top of the booking panel's
  navy head.

  On-Demand is always listed. Dedicated is listed only when the parent says
  so (ORDER_FEATURES.dedicatedOrders and the company's dedicated_orders
  setting), so companies outside the pilot see On-Demand alone.

  A tablist with roving focus: arrow keys move between tabs, and only the
  active tab is in the tab order. The active tab is white with a 2 px
  #F4C332 underline; white on #12284A is well above 4.5:1.
-->
<script lang="ts" setup>
import { computed } from "vue";
import type { OrderType } from "../../utils/orderFeatures";

const props = defineProps<{
  modelValue: OrderType;
  showDedicated: boolean;
}>();

const emit = defineEmits<{ (e: "update:modelValue", value: OrderType): void }>();

const TABS: { value: OrderType; label: string; helper: string }[] = [
  { value: "on_demand", label: "On-Demand", helper: "Nearby drivers, now or at a time you set" },
  { value: "dedicated", label: "Dedicated", helper: "For planned runs. Book at least 2 hours ahead; our team assigns the driver." },
];

const tabs = computed(() => TABS.filter((t) => t.value === "on_demand" || props.showDedicated));
const active = computed(() => tabs.value.find((t) => t.value === props.modelValue) ?? tabs.value[0]);

function select(value: OrderType) {
  if (value !== props.modelValue) emit("update:modelValue", value);
}

function onKey(e: KeyboardEvent, i: number) {
  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
  e.preventDefault();
  const n = tabs.value.length;
  const next = tabs.value[(i + (e.key === "ArrowRight" ? 1 : n - 1)) % n];
  select(next.value);
  const el = (e.currentTarget as HTMLElement)?.parentElement?.querySelector<HTMLElement>(`[data-tab="${next.value}"]`);
  el?.focus();
}
</script>

<template>
  <div class="ott">
    <div class="ott__list" role="tablist" aria-label="Order type">
      <button
        v-for="(t, i) in tabs"
        :key="t.value"
        type="button"
        role="tab"
        class="ott__tab"
        :class="{ 'is-active': t.value === active.value }"
        :data-tab="t.value"
        :aria-selected="t.value === active.value"
        :tabindex="t.value === active.value ? 0 : -1"
        @click="select(t.value)"
        @keydown="onKey($event, i)"
      >
        {{ t.label }}
      </button>
    </div>
    <p class="ott__helper">{{ active.helper }}</p>
  </div>
</template>

<style scoped>
.ott {
  padding: 0 0 10px;
  font-family: 'DM Sans', sans-serif;
}
.ott__list {
  display: flex;
  gap: 22px;
}
.ott__tab {
  position: relative;
  min-height: 40px;
  padding: 0 2px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-family: inherit;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
}
.ott__tab:hover { color: #ffffff; }
.ott__tab.is-active { color: #ffffff; }
.ott__tab.is-active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 2px;
  border-radius: 2px;
  background: #F4C332;
}
.ott__tab:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
  border-radius: 4px;
}
.ott__helper {
  margin: 2px 0 0;
  font-size: 0.75rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.7);
}
</style>
